export function hasContent(value) {
	return value !== null && value !== undefined && String(value).trim() !== "";
}

export function firstDefined(...values) {
	return values.find((value) => value !== null && value !== undefined);
}

export function firstNonEmptyArray(...values) {
	return values.find((value) => Array.isArray(value) && value.length > 0) || [];
}

export function formatIntervalSecondsLabel(intervalSeconds, language = "it") {
	const secondsValue = Number(intervalSeconds);
	if (!Number.isFinite(secondsValue) || secondsValue <= 0) {
		return "";
	}

	const minutes = Math.floor(secondsValue / 60);
	const remainingSeconds = secondsValue % 60;

	if (remainingSeconds === 0) {
		if (language === "it") {
			return `${minutes} ${minutes === 1 ? "minuto" : "minuti"}`;
		}
		return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
	}

	return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export function getForTimeRoundsPrefix(
	rounds,
	roundsLabel = null,
	language = "it",
) {
	const rawValue =
		roundsLabel !== null &&
		roundsLabel !== undefined &&
		String(roundsLabel).trim()
			? String(roundsLabel).trim()
			: rounds !== null && rounds !== undefined
				? String(rounds).trim()
				: "";

	if (!rawValue) return "";

	if (/^\d+$/.test(rawValue)) {
		const numericRounds = Number(rawValue);
		if (!Number.isFinite(numericRounds) || numericRounds <= 1) {
			return "";
		}
		const roundsWord = language === "it" ? "round" : "rounds";
		return `${numericRounds} ${roundsWord}`;
	}

	return rawValue;
}

export function getDisplayEmomRounds(exercise) {
	const rawRounds = Array.isArray(exercise?.emom_rounds)
		? exercise.emom_rounds
		: Array.isArray(exercise?.emom_round_templates)
			? exercise.emom_round_templates
			: [];

	if (rawRounds.length === 0) return [];

	return rawRounds
		.filter((round) => round && typeof round === "object")
		.map((round) => {
			const normalizedItems = Array.isArray(round?.items)
				? round.items
						.filter((item) => item && typeof item === "object")
						.map((item) => ({
							item_id: item?.item_id || null,
							exercise_name:
								item?.exercise_name || item?.name || item?.movement || "",
							reps: item?.reps || "",
							load: item?.load || "",
							notes: item?.notes || "",
							is_custom: Boolean(item?.is_custom),
							custom_text:
								item?.custom_text || item?.content || item?.text || "",
						}))
				: [];

			const roundContent = (
				round?.content ||
				round?.custom_text ||
				round?.text ||
				""
			).trim();
			const items =
				normalizedItems.length > 0
					? normalizedItems
					: roundContent
						? [
								{
									item_id: null,
									exercise_name: "",
									reps: "",
									load: "",
									notes: "",
									is_custom: true,
									custom_text: roundContent,
								},
							]
						: [];

			return {
				round_id: round?.round_id || null,
				assignment_type: round?.assignment_type || "cycle",
				specific_minutes: round?.specific_minutes || "",
				custom_label: round?.custom_label || round?.label || "",
				label: round?.label || "",
				items,
			};
		});
}

export { getEmomRoundLabel } from "./emomRoundLabels";

export function formatEmomItemSummary(item, language = "it") {
	if (item?.is_custom) return item?.custom_text || "Custom";
	const parts = [];
	if (item?.reps) parts.push(item.reps);
	if (item?.exercise_name) parts.push(item.exercise_name);
	if (item?.load) parts.push(`@${item.load}`);
	return (
		parts.join(" ").trim() || (language === "it" ? "Esercizio" : "Exercise")
	);
}

export function extractEmomScoreFromNotes(notes) {
	const lines = String(notes || "").split("\n");
	const scoreLine = lines.find((line) =>
		line.toLowerCase().startsWith("score emom:"),
	);
	return scoreLine ? scoreLine.replace(/^[^:]*:\s*/, "").trim() : "";
}

export function stripEmomScoreFromNotes(notes) {
	return String(notes || "")
		.split("\n")
		.filter((line) => !line.toLowerCase().startsWith("score emom:"))
		.join("\n")
		.trim();
}

function parseAmrapRoundItemReps(value) {
	if (value === null || value === undefined) return null;
	const raw = String(value).trim();
	if (!raw) return null;
	const match = raw.match(/\d+/);
	if (!match) return null;
	const parsed = Number(match[0]);
	if (!Number.isFinite(parsed) || parsed <= 0) return null;
	return Math.floor(parsed);
}

function parseIntegerTotalReps(value) {
	if (value === null || value === undefined) return null;
	const raw = String(value).trim();
	if (!raw) return null;
	const compact = raw.replace(/,/g, ".");
	const fullNumber = compact.match(/^\d+(?:\.0+)?$/);
	const embeddedInteger = compact.match(/\b(\d+)\b/);
	const parsed = Number(fullNumber ? compact : embeddedInteger?.[1]);
	if (!Number.isFinite(parsed) || parsed < 0) return null;
	if (!Number.isInteger(parsed)) return null;
	return parsed;
}

export function extractAmrapTotalRepsFromSchemaValues(
	schemaValues,
	schemaFields,
) {
	if (!schemaValues || typeof schemaValues !== "object") return null;
	const fields = Array.isArray(schemaFields) ? schemaFields : [];
	const candidates = [];

	fields.forEach((field) => {
		if (!field || typeof field !== "object") return;
		const fieldId = String(field.field_id || field.name || "").trim();
		if (!fieldId) return;
		const raw = schemaValues[fieldId];
		const parsed = parseIntegerTotalReps(raw);
		if (parsed === null) return;
		const unit = String(field.unit || "")
			.trim()
			.toLowerCase();
		const title = String(field.title || field.name || "")
			.trim()
			.toLowerCase();
		const isRepsUnit = unit === "reps";
		const isTotalRepsLike = /(?:tot|total)\s*reps?|reps?\s*(?:tot|total)/i.test(
			title,
		);
		const priority = isTotalRepsLike ? 3 : isRepsUnit ? 2 : 1;
		candidates.push({ parsed, priority });
	});

	if (candidates.length > 0) {
		const maxPriority = Math.max(...candidates.map((item) => item.priority));
		return Math.max(
			...candidates
				.filter((item) => item.priority === maxPriority)
				.map((item) => item.parsed),
		);
	}

	const fallbackCandidates = Object.entries(schemaValues)
		.filter(([key]) => /reps?/i.test(String(key || "")))
		.map(([, value]) => parseIntegerTotalReps(value))
		.filter((value) => value !== null);
	if (fallbackCandidates.length > 0) {
		return Math.max(...fallbackCandidates);
	}

	return null;
}

export function getAmrapRoundRepsTotal(conditioningExercises) {
	const rows = Array.isArray(conditioningExercises)
		? conditioningExercises
		: [];
	if (rows.length === 0) return null;

	let sum = 0;
	for (const row of rows) {
		const reps = parseAmrapRoundItemReps(row?.reps);
		if (reps === null) return null;
		sum += reps;
	}

	return sum > 0 ? sum : null;
}

export function formatAmrapRoundsRepsLabel(
	totalReps,
	conditioningExercises,
	language = "it",
) {
	const numericTotal = Number(totalReps);
	if (!Number.isFinite(numericTotal) || numericTotal < 0) return "";
	if (!Number.isInteger(numericTotal)) return "";

	const roundTotal = getAmrapRoundRepsTotal(conditioningExercises);
	if (!roundTotal) return "";

	const roundsCompleted = Math.floor(numericTotal / roundTotal);
	const remainingReps = numericTotal % roundTotal;

	if (remainingReps === 0) {
		if (language === "it") {
			return `${roundsCompleted} ${roundsCompleted === 1 ? "round" : "rounds"}`;
		}
		return `${roundsCompleted} ${roundsCompleted === 1 ? "round" : "rounds"}`;
	}

	return `${roundsCompleted} + ${remainingReps}`;
}

export function formatAmrapScoreWithRoundsLabel(
	totalReps,
	conditioningExercises,
	language = "it",
) {
	const numericTotal = Number(totalReps);
	if (!Number.isFinite(numericTotal) || numericTotal < 0) return "";
	if (!Number.isInteger(numericTotal)) return "";

	const base = `${numericTotal} reps`;
	const roundsLabel = formatAmrapRoundsRepsLabel(
		numericTotal,
		conditioningExercises,
		language,
	);
	if (!roundsLabel) return base;
	return `${base} (${roundsLabel})`;
}

export function resolveConditioningResultPresentation({
	result,
	prescriptionExercise,
	programExercise,
	language = "it",
}) {
	const cfg = result?.conditioning_config || null;
	const condType =
		firstDefined(
			cfg?.conditioning_type,
			prescriptionExercise?.conditioning_type,
			result?.conditioning_type,
			programExercise?.conditioning_type,
		) || null;
	const isConditioning =
		result?.exercise_type === "conditioning" ||
		programExercise?.exercise_type === "conditioning" ||
		hasContent(condType);
	const condExercises = firstNonEmptyArray(
		cfg?.conditioning_exercises,
		prescriptionExercise?.conditioning_exercises,
		programExercise?.conditioning_exercises,
	);
	const resultSchema = firstNonEmptyArray(
		cfg?.result_schema,
		prescriptionExercise?.result_schema,
		programExercise?.result_schema,
	);
	const emomRounds =
		condType === "emom"
			? firstNonEmptyArray(
					getDisplayEmomRounds(cfg),
					getDisplayEmomRounds(prescriptionExercise),
					getDisplayEmomRounds(programExercise),
				)
			: [];
	const rounds = firstDefined(
		cfg?.rounds,
		prescriptionExercise?.rounds,
		programExercise?.rounds,
	);
	const roundsLabel = firstDefined(
		cfg?.rounds_label,
		prescriptionExercise?.rounds_label,
		programExercise?.rounds_label,
	);
	const timeCap = firstDefined(
		cfg?.time_cap_seconds,
		prescriptionExercise?.time_cap_seconds,
		programExercise?.time_cap_seconds,
	);
	const interval = firstDefined(
		cfg?.interval_seconds,
		prescriptionExercise?.interval_seconds,
		programExercise?.interval_seconds,
	);
	const scoreType = String(
		firstDefined(
			cfg?.score_type,
			prescriptionExercise?.score_type,
			result?.score_type,
			programExercise?.score_type,
		) || "",
	)
		.trim()
		.toLowerCase();
	const emomScoreFromNotes =
		condType === "emom" ? extractEmomScoreFromNotes(result?.notes) : "";
	const amrapLikeFromCustomTotalReps =
		scoreType === "time_or_reps" &&
		(condType === "amrap" || condType === "free" || condType === "custom");
	const isAmrapLikeResult =
		condType === "amrap" || amrapLikeFromCustomTotalReps;
	const schemaAmrapTotalReps = extractAmrapTotalRepsFromSchemaValues(
		result?.schema_values,
		resultSchema,
	);
	const amrapTotalReps =
		parseIntegerTotalReps(result?.score) ??
		parseIntegerTotalReps(result?.reps) ??
		parseIntegerTotalReps(result?.reps_text) ??
		schemaAmrapTotalReps;
	const displayScore = hasContent(result?.score)
		? result.score
		: hasContent(result?.reps)
			? result.reps
			: emomScoreFromNotes;
	const amrapRoundsRepsLabel = isAmrapLikeResult
		? formatAmrapRoundsRepsLabel(amrapTotalReps, condExercises, language) ||
			(hasContent(result?.reps_text) ? String(result.reps_text) : "")
		: "";
	const amrapScoreLabel = isAmrapLikeResult
		? formatAmrapScoreWithRoundsLabel(amrapTotalReps, condExercises, language)
		: "";
	const displayNotes =
		condType === "emom"
			? stripEmomScoreFromNotes(result?.notes)
			: result?.notes;
	const intervalLabel = formatIntervalSecondsLabel(interval, language);
	const freeText =
		firstDefined(
			cfg?.conditioning_free_text,
			prescriptionExercise?.conditioning_free_text,
			programExercise?.conditioning_free_text,
			cfg?.custom_workout_text,
			prescriptionExercise?.custom_workout_text,
			programExercise?.custom_workout_text,
		) || "";

	return {
		cfg,
		condType,
		isConditioning,
		condExercises,
		emomRounds,
		rounds,
		roundsLabel,
		timeCap,
		interval,
		intervalLabel,
		displayScore,
		amrapRoundsRepsLabel,
		amrapScoreLabel,
		displayNotes,
		freeText,
	};
}
